'use client'

import { useRef } from 'react'
import { flushSync } from 'react-dom'
import { useTheme } from '@/components/layout/ThemeProvider'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useActualTheme } from '@/stores/themeStore'

type props = {
  className?: string
}

export const AnimatedThemeToggler = ({ className }: props) => {
  const actualTheme = useActualTheme()
  const { toggleTheme } = useTheme()
  const switchRef = useRef<HTMLButtonElement | null>(null)

  const changeTheme = async () => {
    if (!switchRef.current) return

    // Check if browser supports View Transitions API
    if (!document.startViewTransition) {
      toggleTheme()
      return
    }

    // Capture direction BEFORE the toggle: going to dark blossoms the new
    // theme outward; going back to light plays the exact reverse — the old
    // (dark) theme collapses back into the toggle, revealing light beneath.
    const goingDark = actualTheme !== 'dark'

    await document.startViewTransition(() => {
      flushSync(() => {
        toggleTheme()
      })
    }).ready

    const { top, left, width, height } =
      switchRef.current.getBoundingClientRect()
    const y = top + height / 2
    const x = left + width / 2

    const right = window.innerWidth - left
    const bottom = window.innerHeight - top
    const maxRad = Math.hypot(Math.max(left, right), Math.max(top, bottom))

    const full = `circle(${maxRad}px at ${x}px ${y}px)`
    const point = `circle(0px at ${x}px ${y}px)`

    if (goingDark) {
      // Blossom: new (dark) view expands from the toggle.
      document.documentElement.animate(
        { clipPath: [point, full] },
        {
          duration: 700,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      )
    } else {
      // Collapse: old (dark) view contracts back into the toggle, uncovering
      // the light view underneath. `.theme-collapse` raises the old snapshot
      // above the new one so the contraction is visible (see index.css).
      const root = document.documentElement
      root.classList.add('theme-collapse')

      const animation = root.animate(
        { clipPath: [full, point] },
        {
          duration: 700,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-old(root)',
        }
      )

      animation.finished.finally(() => root.classList.remove('theme-collapse'))
    }
  }

  return (
    <Switch
      ref={switchRef}
      checked={actualTheme === 'dark'}
      onCheckedChange={changeTheme}
      className={cn(className)}
    />
  )
}
